import * as THREE from "three";

/** L'horloge du vent, en secondes : celle des animations du décor. */
export const windUniforms = { uWindTime: { value: 0 } };

/** Écart du sommet d'un arbre au plus fort d'une rafale, en part de sa hauteur. */
const BEND_PER_HEIGHT = 0.05;
const LEAF_MATERIAL = /leaf|leav|needle|blossom|petal|grass|plant/i;

const WIND_VERTEX_HEAD = /* glsl */ `
  uniform float uWindTime;
  attribute vec3 windX;
  attribute vec3 windZ;
`;

const WIND_VERTEX_BODY = /* glsl */ `
  #include <begin_vertex>
  #ifdef USE_INSTANCING
    vec2 windOrigin = (modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xz;
  #else
    vec2 windOrigin = (modelMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xz;
  #endif
  float windGust = 0.5 + 0.5 * sin(uWindTime * 0.45 - dot(windOrigin, vec2(0.06, 0.04)));
  float windWave = sin(uWindTime * 1.1 - dot(windOrigin, vec2(0.21, 0.14)));
  float windSway = windWave * (0.3 + 0.7 * windGust);
  transformed += windX * windSway + windZ * (0.6 * windSway + 0.25 * sin(uWindTime * 0.8 + windOrigin.x));
  #ifdef WIND_LEAVES
    float windFlutter = sin(uWindTime * 5.3 + dot(position, vec3(41.0, 29.0, 53.0)) * 7.0);
    transformed += (windX + windZ) * 0.3 * windFlutter * (0.4 + 0.6 * windGust);
  #endif
`;

/**
 * Le vent courbe la plante depuis son pied, plus fort vers sa cime, et agite ses feuilles ; tout se
 * calcule sur la carte graphique, les lots instanciés compris. Rend vrai si quelque chose bouge.
 */
export function prepareWind(root: THREE.Object3D, height: number): boolean {
  if (height <= 0) return false;
  root.updateMatrixWorld(true);
  const toRoot = new THREE.Matrix4();
  const inverseRoot = root.matrixWorld.clone().invert();
  let windy = false;
  root.traverse((node) => {
    if (!(node instanceof THREE.Mesh) || node instanceof THREE.SkinnedMesh) return;
    const geometry = node.geometry as THREE.BufferGeometry;
    const position = geometry.getAttribute("position");
    if (!position) return;
    toRoot.multiplyMatrices(inverseRoot, node.matrixWorld);
    const toLocal = new THREE.Matrix3().setFromMatrix4(toRoot).invert();
    const axisX = new THREE.Vector3(1, 0, 0).applyMatrix3(toLocal);
    const axisZ = new THREE.Vector3(0, 0, 1).applyMatrix3(toLocal);
    if (!geometry.getAttribute("windX")) {
      const windX = new Float32Array(position.count * 3);
      const windZ = new Float32Array(position.count * 3);
      const point = new THREE.Vector3();
      for (let index = 0; index < position.count; index++) {
        point.fromBufferAttribute(position, index).applyMatrix4(toRoot);
        const ratio = Math.min(1, Math.max(0, point.y / height));
        const bend = ratio * ratio * BEND_PER_HEIGHT * height;
        windX.set([axisX.x * bend, axisX.y * bend, axisX.z * bend], index * 3);
        windZ.set([axisZ.x * bend, axisZ.y * bend, axisZ.z * bend], index * 3);
      }
      geometry.setAttribute("windX", new THREE.BufferAttribute(windX, 3));
      geometry.setAttribute("windZ", new THREE.BufferAttribute(windZ, 3));
    }
    const materials: THREE.Material[] = Array.isArray(node.material) ? node.material : [node.material];
    for (const material of materials) windMaterial(material);
    windy = true;
  });
  return windy;
}

function windMaterial(material: THREE.Material): void {
  if (material.userData.wind) return;
  material.userData.wind = true;
  const leaves = LEAF_MATERIAL.test(material.name);
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uWindTime = windUniforms.uWindTime;
    if (leaves) shader.defines = { ...shader.defines, WIND_LEAVES: "" };
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", `#include <common>\n${WIND_VERTEX_HEAD}`)
      .replace("#include <begin_vertex>", WIND_VERTEX_BODY);
  };
  material.customProgramCacheKey = () => (leaves ? "wind-leaves" : "wind");
  material.needsUpdate = true;
}
